// mapbox-lib
import { geojsonTypes, modes, cursors } from '@/vue3/mapbox/constants';
import doubleClickZoom from '@/vue3/mapbox/lib/doubleClickZoom.js';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import * as CommonSelectors from '@/vue3/mapbox/lib/commonSelectors';
import {
    addPointToVertices,
    createSnapList,
    getGuideFeature,
    IDS,
    shouldHideGuide,
    snap,
} from '../mapbox/lib/snapFeature';

import { useStore } from '@/store';
import { getPolygonEdgeLabelFeature } from './supportFunctions';

const store = useStore();

let isMeters;

const DrawPolygon = MapboxDraw.modes.draw_polygon;
const SnapPolygonMode = { ...DrawPolygon };

SnapPolygonMode.onSetup = function (options) {
    const polygon = this.newFeature({
        type: geojsonTypes.FEATURE,
        properties: { isGeometryValid: true },
        geometry: {
            type: geojsonTypes.POLYGON,
            coordinates: [[]],
        },
    });

    const verticalGuide = this.newFeature(getGuideFeature(IDS.VERTICAL_GUIDE));
    const horizontalGuide = this.newFeature(
        getGuideFeature(IDS.HORIZONTAL_GUIDE),
    );

    this.addFeature(polygon);
    this.addFeature(verticalGuide);
    this.addFeature(horizontalGuide);

    const selectedFeatures = this.getSelected();
    this.clearSelectedFeatures();
    doubleClickZoom.disable(this);

    const [snapList, vertices] = createSnapList(
        this.map,
        this._ctx.api,
        polygon,
    );

    const state = {
        map: this.map,
        polygon,
        currentVertexPosition: 0,
        vertices,
        snapList,
        selectedFeatures,
        verticalGuide,
        horizontalGuide,
        options,
    };
    state.options = this._ctx.options;

    const moveEndCallBack = () => {
        const [snapList, vertices] = createSnapList(
            this.map,
            this._ctx.api,
            polygon,
        );
        state.vertices = vertices;
        state.snapList = snapList;
    };
    // for removing listener later on close
    state['moveEndCallBack'] = moveEndCallBack;

    const optionsChangedCallBAck = (options) => {
        state.options = options;
    };

    // for removing listener later on close
    state['optionsChangedCallBAck'] = optionsChangedCallBAck;

    this.map.on('moveend', moveEndCallBack);
    this.map.on('draw.snap.options_changed', optionsChangedCallBAck);

    return state;
};

SnapPolygonMode.onKeyUp = function (state, e) {
    const isEscapeKey = CommonSelectors.isEscapeKey(e);
    if (!isEscapeKey) return;
    this.deleteFeature([state.polygon.id], { silent: true });
    this.changeMode("SnapPolygonMode");
};

SnapPolygonMode.onClickChangeMode = function (state) {
    return this.changeMode(modes.SIMPLE_SELECT, {
        featureIds: [state.polygon.id],
    });
};

SnapPolygonMode.onClickAddVertex = function (state, lng, lat) {
    addPointToVertices(state.map, state.vertices, { lng, lat });

    state.polygon.updateCoordinate(
        `0.${state.currentVertexPosition}`,
        lng,
        lat,
    );

    state.currentVertexPosition++;

    state.polygon.updateCoordinate(
        `0.${state.currentVertexPosition}`,
        lng,
        lat,
    );
};

SnapPolygonMode.onClick = function (state) {
    // We save some processing by rounding on click, not mousemove
    const lng = state.snappedLng;
    const lat = state.snappedLat;

    // End the drawing if this click is on the previous position
    if (state.currentVertexPosition > 0) {
        const lastVertex =
            state.polygon.coordinates[0][state.currentVertexPosition - 1];

        state.lastVertex = lastVertex;

        if (lastVertex[0] !== lng || lastVertex[1] !== lat)
            return this.onClickAddVertex(state, lng, lat);

        if (state.polygon.coordinates.length && state.polygon.coordinates[0].length >= 4)
            return this.onClickChangeMode(state);

        while (state.polygon.coordinates.length && state.polygon.coordinates[0].length < 4) {
            state.polygon.coordinates[0].push([lng, lat]);
        }

        state.polygon.properties.isGeometryValid = false;

        return this.onClickChangeMode(state);
    }

    this.onClickAddVertex(state, lng, lat);
};

SnapPolygonMode.onMouseMove = function (state, e) {
    const { lng, lat } = snap(state, e);

    state.polygon.updateCoordinate(
        `0.${state.currentVertexPosition}`,
        lng,
        lat,
    );
    state.snappedLng = lng;
    state.snappedLat = lat;

    if (
        state.lastVertex &&
        state.lastVertex[0] === lng &&
        state.lastVertex[1] === lat
    ) {
        this.updateUIClasses({ mouse: cursors.POINTER });
    } else {
        this.updateUIClasses({ mouse: cursors.ADD });
    }
};

// This is 'extending' DrawPolygon.toDisplayFeatures
SnapPolygonMode.toDisplayFeatures = function (state, geojson, display) {
    if (shouldHideGuide(state, geojson)) return;

    //------------------------------------------BEGIN-EDGE-LABEL-----------------------------------------------------------------
    const coordinateCount = (((geojson || {}).geometry || {}).coordinates || [
        [],
    ])[0].length;
    if (coordinateCount > 2) {
        isMeters = store.state.isMeters;
        for (let i = 2; i < coordinateCount + 1; i++) {
            let point_1_idx = i - 2;
            let point_2_idx = i - 1;

            if (i == coordinateCount) {
                //For the length of the last line
                if (coordinateCount > 3) {
                    point_2_idx = 0;
                } else {
                    continue;
                }
            }

            const currentLineLabel = getPolygonEdgeLabelFeature(
                geojson,
                point_1_idx,
                point_2_idx,
                isMeters,
            );

            display(currentLineLabel);
        }
    }
    //--------------------------------------------END-EDGE-LABEL-----------------------------------------------------------------

    // This relies on the the state of SnapPolygonMode being similar to DrawPolygon
    DrawPolygon.toDisplayFeatures(state, geojson, display);
};

// This is 'extending' DrawPolygon.onStop
SnapPolygonMode.onStop = function (state) {
    this.deleteFeature(IDS.VERTICAL_GUIDE, { silent: true });
    this.deleteFeature(IDS.HORIZONTAL_GUIDE, { silent: true });

    // remove moveemd callback
    this.map.off('moveend', state.moveEndCallBack);
    this.map.off('draw.snap.options_changed', state.optionsChangedCallBAck);

    // This relies on the the state of SnapPolygonMode being similar to DrawPolygon
    DrawPolygon.onStop.call(this, state);
};

export default SnapPolygonMode;
